import React, { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  MenuItem,
  useTheme
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { PaymentFormData } from "./types/payment";
import { PaymentMethod, PaymentMethodLabels } from "./enums/paymentMethod";
import { useRecordPayment } from "../../hooks/api/useInvoice";
import type { RecordPaymentInput } from "../Invoice/types/invoice";
import { useReportError } from "../../hooks/useReportError";
import { Invoice } from "../Invoice/types/invoice";

interface CreatePaymentModalProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice;
  /** After a successful recordPayment (e.g. refetch order detail so status updates without full reload). */
  onPaymentSuccess?: () => void | Promise<void>;
}

const CreatePaymentModal: React.FC<CreatePaymentModalProps> = ({
  open,
  onClose,
  invoice,
  onPaymentSuccess
}) => {
  const createPayment = useRecordPayment();
  const reportError = useReportError();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();

  // Create schema inside component to access invoice prop and translations
  const paymentSchema = useMemo(
    () =>
      Yup.object<PaymentFormData>().shape({
        invoiceId: Yup.string().required(
          t("invoice-id-required", "Invoice ID is required")
        ),
        amount: Yup.number()
          .typeError(t("amount-must-be-number", "Amount must be a number"))
          .required(t("amount-required", "Amount is required"))
          .min(
            0.01,
            t(
              "amount-must-be-greater-than-zero",
              "Amount must be greater than 0"
            )
          )
          .test(
            "not-exceed-due",
            t(
              "payment-cannot-exceed-due",
              "Payment amount cannot exceed amount due"
            ),
            function (value) {
              if (!value) return true;
              return value <= invoice.remainingAmount;
            }
          ),
        paymentMethod: Yup.string().required(
          t("payment-method-required", "Payment method is required")
        ),
        paymentDate: Yup.string().required(
          t("payment-date-required", "Payment date is required")
        ),
        transactionReference: Yup.string().optional(),
        notes: Yup.string().optional()
      }),
    [invoice.remainingAmount]
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<PaymentFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(paymentSchema as any),
    defaultValues: {
      invoiceId: invoice.invoiceId,
      amount: invoice.remainingAmount,
      paymentMethod: PaymentMethod.CASH,
      paymentDate: new Date().toISOString().split("T")[0],
      transactionReference: undefined,
      notes: ""
    }
  });

  const paymentAmount = watch("amount");
  const amountDue = invoice.remainingAmount;

  useEffect(() => {
    if (open) {
      reset({
        invoiceId: invoice.invoiceId,
        amount: invoice.remainingAmount,
        paymentMethod: PaymentMethod.CASH,
        paymentDate: new Date().toISOString().split("T")[0],
        transactionReference: undefined,
        notes: ""
      });
    }
  }, [open, invoice, reset]);

  const onSubmit: SubmitHandler<PaymentFormData> = async (data) => {
    const refParts: string[] = [];
    const tx = data.transactionReference?.trim();
    const notes = data.notes?.trim();
    if (tx) refParts.push(tx);
    if (notes) refParts.push(`Notes: ${notes}`);
    const reference = refParts.length > 0 ? refParts.join(" | ") : undefined;

    const payload: RecordPaymentInput = {
      invoiceId: data.invoiceId,
      amount: data.amount,
      paymentDate: data.paymentDate,
      reference,
      paymentMethod: data.paymentMethod
    };

    try {
      await createPayment.mutateAsync(payload);
    } catch (error) {
      reportError("Failed to create payment (modal)", error, {
        component: "CreatePaymentModal",
        invoiceId: invoice.invoiceId
      });
      return;
    }

    if (onPaymentSuccess) {
      try {
        await onPaymentSuccess();
      } catch (error) {
        reportError(
          "Payment recorded but follow-up refresh failed (modal)",
          error,
          {
            component: "CreatePaymentModal",
            invoiceId: invoice.invoiceId
          }
        );
      }
    }

    reset();
    onClose();
  };

  const modalStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: matchMD ? "95%" : 500,
    maxHeight: "90vh",
    overflowY: "auto" as const,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    p: 4
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mb={2}
        >
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box mb={3}>
          <Typography
            variant="h5"
            fontWeight="bold"
            textAlign="center"
            color="textPrimary"
          >
            {t("create-payment", "Create Payment")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            mt={1}
          >
            {t("invoice", "Invoice")}: {invoice.invoiceNumber}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            mt={0.5}
          >
            {t("amount-due", "Amount Due")}:{" "}
            {invoice.remainingAmount.toFixed(2)}
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}
        >
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("payment-amount", "Payment Amount")}
                type="number"
                fullWidth
                required
                inputProps={{ step: "0.01", min: 0.01 }}
                error={!!errors.amount}
                helperText={errors.amount?.message}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  field.onChange(value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: BRAND_COLOR },
                    "&:hover fieldset": { borderColor: BRAND_HOVER },
                    "&.Mui-focused fieldset": { borderColor: BRAND_COLOR }
                  }
                }}
              />
            )}
          />

          {paymentAmount && paymentAmount > amountDue && (
            <Box sx={{ p: 1.5, bgcolor: "error.light", borderRadius: 1 }}>
              <Typography variant="body2" color="error.dark">
                {t(
                  "payment-exceeds-due",
                  `Payment amount (${paymentAmount.toFixed(2)}) exceeds amount due (${amountDue.toFixed(2)})`,
                  {
                    paymentAmount: paymentAmount.toFixed(2),
                    amountDue: amountDue.toFixed(2)
                  }
                )}
              </Typography>
            </Box>
          )}

          <Controller
            name="paymentMethod"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label={t("payment-method", "Payment Method")}
                fullWidth
                required
                error={!!errors.paymentMethod}
                helperText={errors.paymentMethod?.message}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: BRAND_COLOR },
                    "&:hover fieldset": { borderColor: BRAND_HOVER },
                    "&.Mui-focused fieldset": { borderColor: BRAND_COLOR }
                  }
                }}
              >
                {Object.values(PaymentMethod).map((method) => (
                  <MenuItem key={method} value={method}>
                    {PaymentMethodLabels[method]}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="paymentDate"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("payment-date", "Payment Date")}
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                error={!!errors.paymentDate}
                helperText={errors.paymentDate?.message}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: BRAND_COLOR },
                    "&:hover fieldset": { borderColor: BRAND_HOVER },
                    "&.Mui-focused fieldset": { borderColor: BRAND_COLOR }
                  }
                }}
              />
            )}
          />

          <Controller
            name="transactionReference"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t(
                  "transaction-reference-optional",
                  "Transaction Reference (optional)"
                )}
                fullWidth
                error={!!errors.transactionReference}
                helperText={errors.transactionReference?.message}
              />
            )}
          />

          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("notes-optional", "Notes (optional)")}
                multiline
                rows={3}
                fullWidth
                error={!!errors.notes}
                helperText={errors.notes?.message}
              />
            )}
          />

          <Box display="flex" gap={2} justifyContent="flex-end" mt={3}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                borderColor: BRAND_COLOR,
                color: BRAND_COLOR,
                "&:hover": {
                  backgroundColor: BRAND_HOVER,
                  color: "white"
                },
                textTransform: "none"
              }}
            >
              {t("cancel", "Cancel")}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={createPayment.isPending}
              disabled={!paymentAmount || paymentAmount > amountDue}
              sx={{
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER },
                textTransform: "none"
              }}
            >
              {t("create-payment", "Create Payment")}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreatePaymentModal;
